import { CommonModule } from '@angular/common';
import { ErrorHandler, Injector, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { ModuleRegistry, TreeDataModule } from 'ag-grid-enterprise';
import { Config } from '../app-template/models/config.model';
import { PrimeModule } from '../prime/prime.module';
import { Utils } from '../utils/shared/utils';
import { AceEditorComponent } from './components/ace-editor/ace-editor.component';
import { ActivateUserComponent } from './components/activate-user/activate-user.component';
import { AdminBaseEditComponent } from './components/admin-base-edit/admin-base-edit.component';
import { AdminBaseListComponent } from './components/admin-base-list/admin-base-list.component';
import { AdminObjectEditComponent } from './components/admin-object-edit/admin-object-edit.component';
import { AdminObjectListComponent } from './components/admin-object-list/admin-object-list.component';
import { CheckBoxWithCountComponent } from './components/check-box-with-count/check-box-with-count.component';
import { ColorEditorComponent } from './components/color-editor/color-editor.component';
import { ConfigEditorTreeComponent } from './components/config-editor-tree/config-editor-tree.component';
import { ContainerDialogComponent } from './components/container-dialog/container-dialog.component';
import { CopyClipboardTextComponent } from './components/copy-clipboard-text/copy-clipboard-text.component';
import { CrudEditComponent } from './components/crud-edit/crud-edit.component';
import { CrudListComponent } from './components/crud-list/crud-list.component';
import { DashboardCardComponent } from './components/dashboard-card/dashboard-card.component';
import { DivInnerHtmlComponent } from './components/div-inner-html/div-inner-html.component';
import { EditGeneralUserGroupComponent } from './components/edit-general-user-group/edit-general-user-group.component';
import { EditGeneralUserGroupsComponent } from './components/edit-general-user-groups/edit-general-user-groups.component';
import { EditGeneralUserComponent } from './components/edit-general-user/edit-general-user.component';
import { EditGeneralUsersComponent } from './components/edit-general-users/edit-general-users.component';
import { FirstLoginComponent } from './components/first-login/first-login.component';
import { FormDialogComponent } from './components/form-dialog/form-dialog.component';
import { GeneralAdminComponent } from './components/general-admin/general-admin.component';
import { LoginComponent } from './components/login/login.component';
import { MessageDialogComponent } from './components/message-dialog/message-dialog.component';
import { PlotDefinitionComponent } from './components/plot-definition/plot-definition.component';
import { PlotComponent } from './components/plot/plot.component';
import { ProgressorOverlayComponent } from './components/progressor-overlay/progressor-overlay.component';
import { QuickHelpComponent } from './components/quick-help/quick-help.component';
import { RegisterComponent } from './components/register/register.component';
import { ResetPasswordLinkComponent } from './components/reset-password-link/reset-password-link.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { SmFileUploadInternalComponent } from './components/sm-file-upload-internal/sm-file-upload-internal.component';
import { SmFileUploadComponent } from './components/sm-file-upload/sm-file-upload.component';
import { SmFormFieldInternalComponent } from './components/sm-form-field-internal/sm-form-field-internal.component';
import { SmFormFieldWrapperComponent } from './components/sm-form-field-wrapper/sm-form-field-wrapper.component';
import { SmFormFieldComponent } from './components/sm-form-field/sm-form-field.component';
import { SmFormGroupComponent } from './components/sm-form-group/sm-form-group.component';
import { SmFormComponent } from './components/sm-form/sm-form.component';
import { SmImageUploadComponent } from './components/sm-image-upload/sm-image-upload.component';
import { SmOverviewBoxComponent } from './components/sm-overview-box/sm-overview-box.component';
import { SmSwitchComponent } from './components/sm-switch/sm-switch.component';
import { SmTableCellEditorComponent } from './components/sm-table-cell-editor/sm-table-cell-editor.component';
import { SmTableCellComponent } from './components/sm-table-cell/sm-table-cell.component';
import { SmTableHeaderComponent } from './components/sm-table-header/sm-table-header.component';
import { SmTableRowEditorFormComponent } from './components/sm-table-row-editor-form/sm-table-row-editor-form.component';
import { SmTableComponent } from './components/sm-table/sm-table.component';
import { SmToolbarComponent } from './components/sm-toolbar/sm-toolbar.component';
import { SmTreeTableCellComponent } from './components/sm-tree-table-cell/sm-tree-table-cell.component';
import { StringListEditorComponent } from './components/string-list-editor/string-list-editor.component';
import { TagButtonComponent } from './components/tag-button/tag-button.component';
import { TextDiffComponent } from './components/text-diff/text-diff.component';
import { TreeItemWithIconsComponent } from './components/tree-item-with-icons/tree-item-with-icons.component';
import { WaitContainerComponent } from './components/wait-container/wait-container.component';
import { WaitingFormComponent } from './components/waiting-form/waiting-form.component';
import { DisableDirective } from './directives/disable.directive';
import { InsertionPointDirective } from './directives/insertion-point.directive';
import { VarDirective } from './directives/ng-var.directive';
import { SmErrorHandler } from './misc/sm-error-handler';
import { KeepHtmlPipe } from './pipes/keep-html.pipe';
import { ErrorHandlingService } from './services/error-handling.service';
import { FileUploadService } from './services/file-upload.service';
import { LocatorService } from './services/locator.service';
import { MessageDialogService } from './services/message-dialog.service';
import { SmBaseRoutingModule } from './sm-base-routing.module';
import { PrimeLabelDirective } from './directives/prime-label.directive';
import { SmTabViewComponent } from './components/sm-tab-view/sm-tab-view.component';
import { SmTabComponent } from './components/sm-tab/sm-tab.component';
import { CdPausedDirective } from './directives/cd-paused.directive';
import { EditGlobalEMailTemplateComponent } from './components/edit-global-email-template.component/edit-global-email-template.component';
import { EditGlobalEMailTemplatesComponent } from './components/edit-global-email-templates.component/edit-global-email-templates.component';
import { TerminalComponent } from './components/terminal/terminal.component';

@NgModule({
    declarations: [
        MessageDialogComponent,
        LoginComponent,
        VarDirective,
        DisableDirective,
        InsertionPointDirective,
        KeepHtmlPipe,
        SmFormFieldComponent,
        SmFormComponent,
        SmOverviewBoxComponent,
        SmFileUploadComponent,
        SmFileUploadInternalComponent,
        SmImageUploadComponent,
        FormDialogComponent,
        SmFormFieldInternalComponent,
        SmFormGroupComponent,
        SmTableComponent,
        CrudEditComponent,
        RegisterComponent,
        ActivateUserComponent,
        QuickHelpComponent,
        FirstLoginComponent,
        ResetPasswordComponent,
        ResetPasswordLinkComponent,
        ColorEditorComponent,
        StringListEditorComponent,
        WaitingFormComponent,
        CheckBoxWithCountComponent,
        SmFormFieldWrapperComponent,
        SmTreeTableCellComponent,
        DashboardCardComponent,
        TagButtonComponent,
        SmToolbarComponent,
        AceEditorComponent,
        WaitContainerComponent,
        SmSwitchComponent,
        TreeItemWithIconsComponent,
        CopyClipboardTextComponent,
        ProgressorOverlayComponent,
        EditGeneralUsersComponent,
        EditGeneralUserComponent,
        EditGeneralUserGroupsComponent,
        EditGeneralUserGroupComponent,
        EditGlobalEMailTemplateComponent,
        EditGlobalEMailTemplatesComponent,
        ConfigEditorTreeComponent,
        TextDiffComponent,
        PlotComponent,
        PlotDefinitionComponent,
        SmTableCellComponent,
        SmTableHeaderComponent,
        SmTableCellEditorComponent,
        SmTableRowEditorFormComponent,
        DivInnerHtmlComponent,
        GeneralAdminComponent,
        AdminObjectListComponent,
        AdminObjectEditComponent,
        AdminBaseListComponent,
        AdminBaseEditComponent,
        ContainerDialogComponent,
        CrudListComponent,
        SmTabViewComponent,
        SmTabComponent,
        CdPausedDirective,
        TerminalComponent
    ],
    imports: [
        CommonModule,
        SmBaseRoutingModule,
        ReactiveFormsModule,
        PrimeModule,
        AgGridAngular,        
        PrimeLabelDirective
    ],
    exports: [
        SmFormComponent,
        SmToolbarComponent,
        SmOverviewBoxComponent,
        SmFormFieldComponent,
        SmFormFieldInternalComponent,
        SmTableCellComponent,
        SmTreeTableCellComponent,
        SmTableComponent,
        SmFileUploadComponent,
        SmImageUploadComponent,
        MessageDialogComponent,
        VarDirective,
        DisableDirective,
        InsertionPointDirective,
        KeepHtmlPipe,
        CrudEditComponent,
        QuickHelpComponent,
        StringListEditorComponent,
        CheckBoxWithCountComponent,
        DashboardCardComponent,
        TagButtonComponent,
        AceEditorComponent,
        WaitContainerComponent,
        SmSwitchComponent,
        TreeItemWithIconsComponent,
        CopyClipboardTextComponent,
        ProgressorOverlayComponent,
        ConfigEditorTreeComponent,
        TextDiffComponent,
        PlotComponent,
        PlotDefinitionComponent,
        SmTableCellEditorComponent,
        DivInnerHtmlComponent,
        GeneralAdminComponent,
        AdminObjectListComponent,
        AdminObjectEditComponent,
        AdminBaseListComponent,
        AdminBaseEditComponent,
        ContainerDialogComponent,
        CrudListComponent,        
        PrimeLabelDirective,
        SmTabViewComponent,
        SmTabComponent,
        CdPausedDirective,
        TerminalComponent
    ],
    providers: [
        MessageDialogService,
        FileUploadService,
        ErrorHandlingService,
        { provide: ErrorHandler, useClass: SmErrorHandler }
    ]
})
export class SmBaseModule {

    constructor(injector: Injector) {
        LocatorService.injector = injector;

        if (!Utils.isNoe(Config.get().agGridEnterpriseKey)) {
            ModuleRegistry.registerModules([TreeDataModule]);
        }
    }
}
